import * as THREE from 'three';
import { IVideoTextureEngine } from './IVideoTextureEngine';

export class CanvasVideoEngine implements IVideoTextureEngine {
  public texture: THREE.Texture;
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D | null;
  private isActive = false;
  private lastUpdateTime = 0;
  private targetFPS = 30; // Cap at 30fps to save battery since this is CPU heavy
  private frameInterval = 1000 / 30;

  constructor(video: HTMLVideoElement) {
    this.video = video;

    // Create offscreen canvas
    this.canvas = document.createElement('canvas');
    // Default size, will resize to match video once metadata loads
    this.canvas.width = 1024; 
    this.canvas.height = 512;
    this.context = this.canvas.getContext('2d', { willReadFrequently: true });

    // Create CanvasTexture
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;
  }

  start(): void {
    console.log('[CanvasVideoEngine] Started - using 2D Canvas fallback');
    this.isActive = true;
    
    // Ensure canvas dimensions match video when ready
    if (this.video.readyState >= 1) {
      this.resizeCanvas();
    }
    this.video.addEventListener('loadedmetadata', () => this.resizeCanvas());
  }

  update(): void {
    if (!this.isActive || !this.context) return;

    const now = performance.now();
    if (now - this.lastUpdateTime < this.frameInterval) return;

    // Only draw if video is actually playing/ready
    if (this.video.readyState >= 2 && !this.video.paused && !this.video.ended) {
      try {
        // Draw current video frame to canvas
        this.context.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
        
        // Signal texture update
        this.texture.needsUpdate = true;
        this.lastUpdateTime = now;
      } catch (e) {
        // Ignore play interruption errors
      }
    }
  }

  private resizeCanvas() {
    if (this.video.videoWidth > 0 && this.video.videoHeight > 0) {
      // Scale down slightly for performance on older devices if 4K
      // Or keep 1:1 if feasible. Let's cap at 2048px width for safety on old iOS.
      const MAX_WIDTH = 2048;
      let width = this.video.videoWidth;
      let height = this.video.videoHeight;
      
      if (width > MAX_WIDTH) {
        const ratio = MAX_WIDTH / width;
        width = MAX_WIDTH;
        height = Math.floor(height * ratio);
      }

      this.canvas.width = width;
      this.canvas.height = height;
      console.log(`[CanvasVideoEngine] Resized canvas to ${width}x${height}`);
    }
  }

  destroy(): void {
    this.isActive = false;
    this.texture.dispose();
    this.video.removeEventListener('loadedmetadata', () => this.resizeCanvas());
    // Canvas is garbage collected
  }
}
